import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Clock, Bell, Users, ArrowRight, RefreshCw, Database } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { useLang } from "../../contexts/LanguageContext";
import PageHead from "../../components/PageHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [polling, setPolling] = useState(false);
  const { t } = useLang();

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/seed`, {}, { withCredentials: true });
      await fetchStats();
    } catch (e) { console.error(e); }
    finally { setSeeding(false); }
  };

  const handlePoll = async () => {
    setPolling(true);
    try {
      await axios.post(`${API}/recalls/poll`, {}, { withCredentials: true });
      await fetchStats();
    } catch (e) { console.error(e); }
    finally { setPolling(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm" style={{ color: "var(--kyra-mu)" }}>{t("dashboard.loading")}</div>;

  const kpis = [
    { label: t("dashboard.recallsStored"), value: stats?.recalls_count || 0, icon: AlertTriangle, color: "var(--kyra-rd)", bg: "rgba(176,48,37,0.08)" },
    { label: t("dashboard.purchasesIndexed"), value: stats?.purchases_count || 0, icon: Database, color: "var(--kyra-gr)", bg: "rgba(27,67,50,0.08)" },
    { label: t("dashboard.alertsCount"), value: stats?.alerts_count || 0, icon: Bell, color: "var(--kyra-am)", bg: "rgba(168,107,16,0.08)" },
    { label: t("dashboard.dlcCount"), value: stats?.dlc_notifications_count || 0, icon: Clock, color: "var(--kyra-gr)", bg: "rgba(27,67,50,0.08)" },
  ];

  return (
    <div data-testid="dashboard-overview">
      <PageHead title="Dashboard" />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: "'Playfair Display'" }}>{t("dashboard.overview")}</h1>
          <p className="text-sm font-light mt-1" style={{ color: "var(--kyra-mu)" }}>{t("dashboard.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="seed-btn"
            onClick={handleSeed}
            disabled={seeding}
            className="flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-md transition-colors cursor-pointer disabled:opacity-50"
            style={{ background: "var(--kyra-bg2)", color: "var(--kyra-tx)", border: "1px solid var(--kyra-li)" }}
          >
            <Database className="w-3.5 h-3.5" />
            {seeding ? "..." : "Seed demo"}
          </button>
          <button
            data-testid="poll-btn"
            onClick={handlePoll}
            disabled={polling}
            className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-white rounded-md transition-colors cursor-pointer disabled:opacity-50"
            style={{ background: "var(--kyra-gr)", border: "none" }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${polling ? "animate-spin" : ""}`} />
            {polling ? "Polling..." : "Polling RappelConso"}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((k, i) => (
          <div key={i} className="p-5 rounded-lg border" style={{ background: "#fff", borderColor: "var(--kyra-li)" }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: k.bg }}>
                <k.icon className="w-4 h-4" style={{ color: k.color }} />
              </div>
              <span className="text-xs font-medium" style={{ color: "var(--kyra-mu)" }}>{k.label}</span>
            </div>
            <div className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Playfair Display'", color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Alert breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="p-5 rounded-lg border" style={{ background: "#fff", borderColor: "var(--kyra-li)" }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Alertes par type</h3>
          </div>
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: "var(--kyra-rd)" }} />
              <span className="text-xs" style={{ color: "var(--kyra-mu)" }}>Rappels : {stats?.recall_alerts || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: "var(--kyra-gr)" }} />
              <span className="text-xs" style={{ color: "var(--kyra-mu)" }}>DLC : {stats?.dlc_alerts || 0}</span>
            </div>
          </div>
          <div className="flex h-3 rounded-full overflow-hidden mt-3" style={{ background: "var(--kyra-bg2)" }}>
            {stats?.alerts_count > 0 && (
              <>
                <div className="h-full rounded-l-full" style={{ width: `${(stats.recall_alerts / stats.alerts_count) * 100}%`, background: "var(--kyra-rd)" }} />
                <div className="h-full rounded-r-full" style={{ width: `${(stats.dlc_alerts / stats.alerts_count) * 100}%`, background: "var(--kyra-gr)" }} />
              </>
            )}
          </div>
        </div>
        <div className="p-5 rounded-lg border" style={{ background: "#fff", borderColor: "var(--kyra-li)" }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Actions rapides</h3>
          </div>
          <div className="flex flex-col gap-2">
            <Link to="/dashboard/recalls" data-testid="quick-recalls" className="flex items-center justify-between p-3 rounded-md no-underline transition-colors hover:bg-white/50" style={{ background: "var(--kyra-bg)", color: "var(--kyra-tx)" }}>
              <span className="text-xs font-medium">Voir les rappels produits</span>
              <ArrowRight className="w-3.5 h-3.5" style={{ color: "var(--kyra-mu)" }} />
            </Link>
            <Link to="/dashboard/dlc" data-testid="quick-dlc" className="flex items-center justify-between p-3 rounded-md no-underline transition-colors hover:bg-white/50" style={{ background: "var(--kyra-bg)", color: "var(--kyra-tx)" }}>
              <span className="text-xs font-medium">Generer les notifications DLC</span>
              <ArrowRight className="w-3.5 h-3.5" style={{ color: "var(--kyra-mu)" }} />
            </Link>
            <Link to="/dashboard/alerts" data-testid="quick-alerts" className="flex items-center justify-between p-3 rounded-md no-underline transition-colors hover:bg-white/50" style={{ background: "var(--kyra-bg)", color: "var(--kyra-tx)" }}>
              <span className="text-xs font-medium">Historique des alertes</span>
              <ArrowRight className="w-3.5 h-3.5" style={{ color: "var(--kyra-mu)" }} />
            </Link>
          </div>
        </div>
      </div>

      {/* Recent recalls */}
      {stats?.recent_recalls?.length > 0 && (
        <div className="p-5 rounded-lg border" style={{ background: "#fff", borderColor: "var(--kyra-li)" }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Derniers rappels stockes</h3>
            <Link to="/dashboard/recalls" className="text-xs no-underline" style={{ color: "var(--kyra-gr)" }}>Tout voir</Link>
          </div>
          <div className="flex flex-col gap-2">
            {stats.recent_recalls.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-md" style={{ background: "var(--kyra-bg)" }}>
                <div className="w-1 h-8 rounded-full shrink-0" style={{ background: "var(--kyra-rd)" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">{r.modele || r.numero_fiche}</div>
                  <div className="text-[11px] font-light truncate" style={{ color: "var(--kyra-mu)" }}>{r.motif}</div>
                </div>
                <Badge variant="outline" className="text-[10px] shrink-0" style={{ borderColor: "var(--kyra-rd)", color: "var(--kyra-rd)" }}>
                  {r.matched_customers} clients
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
